import {
  GENDER_FEMALE_COLOR,
  GENDER_MALE_COLOR,
} from '../Analytics/CrowdCountSection/constants';

const FACE = 'Face';
const PERSON = 'Person';

/** CSS layout size per tile (16:9). Map scales the sheet — never squash via width %. */
const TILE_CSS_W = 160;
const TILE_CSS_H = Math.round((TILE_CSS_W * 9) / 16);

const BOOL_FACE_ATTRS = [
  'Smile',
  'Eyeglasses',
  'Sunglasses',
  'Beard',
  'Mustache',
  'EyesOpen',
  'MouthOpen',
  'FaceOccluded',
];

const BOX_COLORS = ['#f5c542', '#4db6ff', '#81c784', '#ff8a65', '#ce93d8', '#4dd0e1', '#ffd54f', '#ef9a9a'];

const SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    height: 38,
    borderRadius: 30,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 110,
    borderRadius: 30,
    overflow: 'hidden',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  valueContainer: (base) => ({
    ...base,
    height: 38,
    padding: '0 8px 0 14px',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 38,
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    lineHeight: '20px',
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    lineHeight: '20px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
};

const DETECTABLES_SELECT_STYLES = {
  ...SELECT_STYLES,
  control: (base) => ({
    ...SELECT_STYLES.control(base),
    minWidth: 180,
  }),
  valueContainer: (base) => ({
    ...SELECT_STYLES.valueContainer(base),
    position: 'relative',
    flexWrap: 'nowrap',
  }),
  option: (base, state) => {
    let backgroundColor = 'transparent';
    if (state.isSelected) backgroundColor = '#d9e8ff';
    else if (state.isFocused) backgroundColor = '#f0f0f0';
    return {
      ...base,
      backgroundColor,
      color: '#333',
      cursor: 'pointer',
    };
  },
};

/** Session = header; region rows indented under it. */
const SCAN_SELECT_STYLES = {
  ...SELECT_STYLES,
  option: (base, state) => {
    let backgroundColor = 'transparent';
    if (state.isSelected) backgroundColor = '#d9e8ff';
    else if (state.isFocused) backgroundColor = '#f0f0f0';
    const isRegion = state.data?.kind === 'region';
    return {
      ...base,
      backgroundColor,
      color: '#333',
      cursor: 'pointer',
      fontWeight: isRegion ? 400 : 600,
      paddingLeft: isRegion ? 36 : 12,
    };
  },
};

const colorForLabel = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.codePointAt(i)) % BOX_COLORS.length;
  }
  return BOX_COLORS[Math.abs(hash) % BOX_COLORS.length];
};

/** Face + gender → demographics colors; otherwise label palette. */
const colorForBox = (box) => {
  if (box?.label === FACE && Number.isFinite(box.genderConfidence)) {
    if (box.gender === 'Male') return GENDER_MALE_COLOR;
    if (box.gender === 'Female') return GENDER_FEMALE_COLOR;
  }
  return colorForLabel(box?.label || '?');
};

const fmtPtz = (n) => (Number.isFinite(n) ? String(Math.round(n * 100) / 100) : '');

const tileKey = (tile) => `${tile.row}-${tile.col}-${tile.index}`;

/**
 * Tile grid coordinates:
 * - Stored / keyed / drawn: 0-based (API `tile.row`, `tile.col`, `tile.index`)
 * - Shown to humans: 1-based (r1·c1 is the top-left cell)
 */
const tileRowLabel = (row) => (Number(row) || 0) + 1;
const tileColLabel = (col) => (Number(col) || 0) + 1;
const tileIndexLabel = (index) => (Number(index) || 0) + 1;
const fmtTileRC = (row, col) => `r${tileRowLabel(row)} · c${tileColLabel(col)}`;
const fmtTilePos = (tile) => (
  `${fmtTileRC(tile?.row, tile?.col)} · #${tileIndexLabel(tile?.index)}`
);

/** Lookup key for tileBoxes map (prefixes with scan id in stadium mode). */
const tileBoxesKey = (tile, scanId = tile?._scanId) => (
  scanId ? `${scanId}:${tileKey(tile)}` : tileKey(tile)
);

const sortTiles = (tiles) => [...(tiles || [])].sort((a, b) => a.index - b.index);

const scanIdsFromOption = (opt) => {
  if (!opt) return [];
  return opt.kind === 'session' ? (opt.regionIds || []) : [opt.id];
};

/** Absolute pan/tilt for a mosaic cell. Prefers scanBounds steps (same as SRS);
 * falls back to endpoint lerp for legacy scans without panStep/tiltStep. */
const scanCellPtz = (scan, col, row) => {
  const b = scan?.scanBounds || {};
  const start = b.start || {};
  const panStep = Number(b.panStep ?? scan?.panStep);
  const tiltStep = Number(b.tiltStep ?? scan?.tiltStep);
  const c = Number(col) || 0;
  const r = Number(row) || 0;
  const zoom = Number.isFinite(Number(b.zoom)) ? Number(b.zoom) : null;

  if (Number.isFinite(start.pan) && Number.isFinite(panStep)
    && Number.isFinite(start.tilt) && Number.isFinite(tiltStep)) {
    return {
      pan: start.pan + c * panStep,
      tilt: start.tilt + r * tiltStep,
      zoom,
    };
  }

  const end = b.end || {};
  const rows = scan?.rows || 1;
  const cols = scan?.cols || 1;
  const colT = cols <= 1 ? 0 : c / (cols - 1);
  const rowT = rows <= 1 ? 0 : r / (rows - 1);
  const lerp = (a, b2, t) => (Number.isFinite(a) && Number.isFinite(b2) ? a + (b2 - a) * t : null);
  return {
    pan: lerp(start.pan, end.pan, colT),
    tilt: lerp(start.tilt, end.tilt, rowT),
    zoom,
  };
};

const tilePtz = (tile, scan) => scanCellPtz(scan, tile?.col, tile?.row);

/** Run async work over items with a max concurrency (avoids browser resource errors). */
const mapPool = async (items, concurrency, worker) => {
  const list = items || [];
  if (!list.length) return [];
  const limit = Math.max(1, Math.min(concurrency || 1, list.length));
  const results = new Array(list.length);
  let next = 0;

  const run = async () => {
    while (next < list.length) {
      const i = next;
      next += 1;
      // Sequential slot work within each pool worker.
      // eslint-disable-next-line no-await-in-loop
      results[i] = await worker(list[i], i);
    }
  };

  await Promise.all(Array.from({ length: limit }, () => run()));
  return results;
};

/**
 * Process-wide concurrency gate (shared across all MosaicCanvas instances).
 * Prevents stadium mode from running 10×N image decodes at once.
 */
const globalGates = new Map();

const withGlobalGate = async (name, concurrency, work) => {
  let gate = globalGates.get(name);
  if (!gate) {
    gate = { active: 0, wait: [], limit: Math.max(1, concurrency || 1) };
    globalGates.set(name, gate);
  }
  if (gate.limit !== concurrency && concurrency > 0) {
    gate.limit = concurrency;
  }
  if (gate.active >= gate.limit) {
    await new Promise((resolve) => { gate.wait.push(resolve); });
  }
  gate.active += 1;
  try {
    return await work();
  } finally {
    gate.active -= 1;
    const next = gate.wait.shift();
    if (next) next();
  }
};

const GLOBAL_IMAGE_CONCURRENCY = 6;

export {
  FACE,
  PERSON,
  BOOL_FACE_ATTRS,
  SCAN_SELECT_STYLES,
  DETECTABLES_SELECT_STYLES,
  colorForLabel,
  colorForBox,
  fmtPtz,
  tileKey,
  tileBoxesKey,
  fmtTileRC,
  fmtTilePos,
  sortTiles,
  scanIdsFromOption,
  scanCellPtz,
  tilePtz,
  mapPool,
  withGlobalGate,
  GLOBAL_IMAGE_CONCURRENCY,
  TILE_CSS_W,
  TILE_CSS_H,
};
